/**
 * Pemeta baris katalog PUBLIK (`products` + embed media/varian/grup) → objek
 * `Product` storefront. MURNI — tanpa Supabase/React/I-O — supaya pembaca lain
 * yang WAJIB melihat produk persis seperti halaman produk (feed Katalog
 * Instagram, `metaCatalog.ts`) memakai SATU definisi yang sama.
 *
 * Dipindah APA ADANYA dari `src/lib/supabase/public-data.ts` (PR-IG-1): badan
 * `mapPublicProduct`, `mapPublicVariant`, `sortProductImages`, dan tipe
 * `PublicProductRow` tidak berubah satu baris pun — hanya diekspor. Alasannya:
 * `public-data.ts` mengimpor klien anon (`@/lib/supabase/client`), sehingga
 * jalur server yang tidak boleh menyentuh klien itu (route feed) tak bisa
 * memakai pemetanya tanpa menyeret Supabase ikut dimuat.
 *
 * Perubahan sesudahnya: (PR-IG-3, F5) urutan `sort_order` yang KEMBAR dipecah
 * dengan `id` — lihat `bySortOrderThenId`; (mig 6840) `priceEstimate`.
 */

import {
  type PreorderStatus,
  type Product,
  type VariantGroup,
} from "@/lib/products";
import { resolveProductMediaUrl } from "@/lib/mediaUrl";
import { parsePriceEstimate } from "@/lib/priceEstimate";

export type PublicProductRow = {
  id: string;
  slug: string;
  name: string;
  brand: string | null;
  category?: string | null;
  segment?: string | null;
  description: string | null;
  preorder_deadline: string | null;
  estimated_ready: string | null;
  status: PreorderStatus;
  product_type?: "preorder" | "ready_stock" | null;
  po_limited?: boolean | null;
  /** Mode daftar kombinasi (B1) — `products.absent_combos_sellable`. */
  absent_combos_sellable?: boolean | null;
  price_normal?: number | null;
  price_reseller?: number | null;
  price_agen?: number | null;
  /** mig 6840 — hanya jalur DETAIL & feed Katalog Instagram; listing tak menyeleksinya. */
  price_estimate?: number | string | null;
  reseller_discount: string | null;
  agent_discount: string | null;
  dp_required?: boolean | null;
  dp_amount?: number | null;
  dp_mode?: string | null;
  product_media?: Array<{
    media_type: "image" | "video";
    media_url: string;
    storage_path?: string | null;
    is_cover?: boolean | null;
    sort_order: number;
    /** mig 5890 — hanya ikut di jalur DETAIL; listing tak menyeleksinya. */
    is_size_guide?: boolean | null;
  }>;
  product_variants?: Array<{
    id: string;
    name: string;
    sku: string | null;
    option_ids: string[] | null;
    price_normal?: number | null;
    price_extra?: number | null;
    price_reseller?: number | null;
    price_agen?: number | null;
    stock?: number | null;
    po_quota?: number | null;
    weight?: number | null;
    diskon_mode?: string | null;
    diskon_reseller?: number | null;
    diskon_agen?: number | null;
    diskon_slot3?: number | null;
    diskon_slot4?: number | null;
    diskon_slot5?: number | null;
    diskon_slot6?: number | null;
    diskon_slot7?: number | null;
    diskon_slot8?: number | null;
    diskon_slot9?: number | null;
    diskon_slot10?: number | null;
    diskon_slot11?: number | null;
    diskon_slot12?: number | null;
    diskon_slot13?: number | null;
    diskon_slot14?: number | null;
    diskon_slot15?: number | null;
    sort_order: number;
  }>;
  variant_groups?: Array<{
    id: string;
    name: string;
    sort_order: number;
    use_as_image_primary: boolean;
    variant_options: Array<{
      id: string;
      name: string;
      image_url: string | null;
      sort_order: number;
    }>;
  }>;
};

/**
 * Urutan admin: `sort_order`, lalu `id` (F5, PR-IG-3). Tanpa pemecah seri,
 * baris ber-`sort_order` kembar tiba dalam urutan embed PostgREST yang TAK
 * ditentukan query — varian awal halaman, varian perwakilan harga, dan urutan
 * tombol opsi bisa berganti antar-render (PROD 2026-09-29: 5 produk ber-varian
 * kembar, satu dengan 15 varian ber-`sort_order` 0 dan 14 harga berbeda; 1 grup
 * ber-opsi kembar). Feed Katalog Instagram (SQL `order by sort_order, id`) dan
 * kartu (`public-data.ts`) sudah memakai urutan ini, jadi halaman produk kini
 * melihat varian dalam urutan yang SAMA dengan keduanya. Pembanding string
 * biasa (bukan `localeCompare`): UUID huruf kecil berurut sama dengan `uuid`
 * Postgres (byte demi byte).
 */
function bySortOrderThenId(
  a: { sort_order: number; id: string },
  b: { sort_order: number; id: string }
): number {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function mapPublicProduct(row: PublicProductRow): Product {
  const media = [...(row.product_media ?? [])];
  const variants = [...(row.product_variants ?? [])].sort(bySortOrderThenId);
  // URL gambar disusun dari project AKTIF + storage_path (mig 2300); baris
  // belum ter-backfill fallback ke media_url apa adanya.
  const imageMedia = media
    .filter((item) => item.media_type === "image")
    .sort(sortProductImages);
  const images = imageMedia.map(
    (item) => resolveProductMediaUrl(item) ?? item.media_url
  );

  // Foto PANDUAN UKURAN (mig 5890) — kemunculan PERTAMA dalam urutan tayang
  // yang SAMA dengan `images` di atas. Sengaja diturunkan dari array yang sudah
  // terurut, bukan dari `media` mentah: DB tak menjamin hanya ada satu baris
  // bertanda (alasannya di kepala mig 5890), jadi yang menjamin pembeli melihat
  // foto yang sama di tiap render adalah urutan ini. `undefined` = tak ada →
  // `ProductDetail` tak merender tombolnya sama sekali.
  const sizeGuideIndex = imageMedia.findIndex((item) => item.is_size_guide);
  const sizeGuideImage =
    sizeGuideIndex >= 0 ? images[sizeGuideIndex]?.trim() || undefined : undefined;

  // Bangun variantGroups — sort grup, lalu sort options dalam tiap grup
  const variantGroups: VariantGroup[] = [...(row.variant_groups ?? [])]
    .sort(bySortOrderThenId)
    .map((g) => ({
      id: g.id,
      name: g.name,
      sortOrder: g.sort_order,
      useAsImagePrimary: g.use_as_image_primary,
      options: [...g.variant_options]
        .sort(bySortOrderThenId)
        .map((o) => ({
          id: o.id,
          name: o.name,
          imageUrl: o.image_url ?? undefined,
          sortOrder: o.sort_order,
        })),
    }));

  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    brand: row.brand ?? "",
    category: row.category ?? undefined,
    segment: row.segment ?? undefined,
    deadline: row.preorder_deadline ?? "",
    estimatedReady: row.estimated_ready ?? "",
    status: row.status,
    productType: row.product_type ?? "preorder",
    poLimited: row.po_limited ?? false,
    absentCombosSellable: row.absent_combos_sellable ?? null,
    price_normal: row.price_normal ?? undefined,
    price_reseller: row.price_reseller ?? undefined,
    price_agen: row.price_agen ?? undefined,
    // Angka mentah; BERLAKU-tidaknya diputuskan `effectivePriceEstimate`.
    priceEstimate: parsePriceEstimate(row.price_estimate),
    description: row.description ?? "",
    resellerDiscount: row.reseller_discount ?? undefined,
    agentDiscount: row.agent_discount ?? undefined,
    dpRequired: row.dp_required ?? false,
    dpAmount: row.dp_amount ?? undefined,
    dpMode: (row.dp_mode ?? "order") as Product["dpMode"],
    // Produk TANPA foto memulangkan array KOSONG — jangan diganti foto sample.
    // Fallback-nya visual, bukan data: `ProductPhotoFallback` menampilkan LOGO
    // TOKO (site_settings `store_logo_url`) redup di tengah bingkai, dan itu
    // hanya menyala kalau `images` benar-benar kosong. Dulu di sini disuntikkan
    // satu URL Unsplash (foto tas), yang membuat fallback logo tak pernah
    // kebagian jalan dan produk toko mana pun tampil memakai foto barang orang
    // lain. Semua pembaca `images[0]` sudah toleran array kosong.
    images,
    sizeGuideImage,
    variations: variants.map(mapPublicVariant),
    variantGroups,
  };
}

/**
 * Baris `product_variants` → `ProductVariation`. SATU definisi dipakai jalur
 * embed (mapPublicProduct) DAN resolusi on-demand (resolvePublicVariant):
 * kalau keduanya memetakan sendiri-sendiri, varian hasil resolusi bisa
 * kehilangan field diskon dan diam-diam dihargai berbeda dari varian yang
 * datang lewat embed.
 */
export function mapPublicVariant(
  variant: NonNullable<PublicProductRow["product_variants"]>[number]
): Product["variations"][number] {
  return {
    id: variant.id,
    name: variant.name,
    sku: variant.sku ?? variant.name,
    optionIds: variant.option_ids ?? [],
    price_normal: variant.price_normal ?? undefined,
    price_extra: variant.price_extra ?? undefined,
    price_reseller: variant.price_reseller ?? undefined,
    price_agen: variant.price_agen ?? undefined,
    stockQty: variant.stock ?? undefined,
    poQuota: variant.po_quota ?? undefined,
    weightGram: variant.weight ?? undefined,
    diskonMode:
      variant.diskon_mode === "nominal" || variant.diskon_mode === "persen"
        ? variant.diskon_mode
        : undefined,
    diskonReseller: variant.diskon_reseller ?? undefined,
    diskonAgen: variant.diskon_agen ?? undefined,
    diskonSlot3: variant.diskon_slot3 ?? undefined,
    diskonSlot4: variant.diskon_slot4 ?? undefined,
    diskonSlot5: variant.diskon_slot5 ?? undefined,
    diskonSlot6: variant.diskon_slot6 ?? undefined,
    diskonSlot7: variant.diskon_slot7 ?? undefined,
    diskonSlot8: variant.diskon_slot8 ?? undefined,
    diskonSlot9: variant.diskon_slot9 ?? undefined,
    diskonSlot10: variant.diskon_slot10 ?? undefined,
    diskonSlot11: variant.diskon_slot11 ?? undefined,
    diskonSlot12: variant.diskon_slot12 ?? undefined,
    diskonSlot13: variant.diskon_slot13 ?? undefined,
    diskonSlot14: variant.diskon_slot14 ?? undefined,
    diskonSlot15: variant.diskon_slot15 ?? undefined,
  };
}

export function sortProductImages(
  first: { is_cover?: boolean | null; sort_order: number },
  second: { is_cover?: boolean | null; sort_order: number }
) {
  if (Boolean(first.is_cover) !== Boolean(second.is_cover)) {
    return Number(Boolean(second.is_cover)) - Number(Boolean(first.is_cover));
  }

  return first.sort_order - second.sort_order;
}
